import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/utils';
import { typographyVariants } from './Typography.variants';
import { variantElementMap } from './types/typography';

export interface TypographyProps
  extends
    Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof typographyVariants> {
  as?: React.ElementType;
  asChild?: boolean;
}

/**
 * Typography component for rendering text with consistent styling.
 * Supports polymorphic rendering via 'as' prop and composition via 'asChild'.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Typography variant="h1">Page Title</Typography>
 *
 * // With custom element
 * <Typography as="span" variant="muted">Inline text</Typography>
 *
 * // With composition
 * <Typography variant="h2" asChild>
 *   <Link to="/page">Clickable Heading</Link>
 * </Typography>
 * ```
 */
const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      className,
      variant = 'p',
      color,
      align,
      weight,
      size,
      as,
      asChild = false,
      children,
      ...props
    },
    ref,
  ) => {
    // Determine the element to render
    const Component = asChild
      ? Slot
      : as || variantElementMap[variant || 'p'] || 'p';

    return (
      <Component
        ref={ref}
        className={cn(
          typographyVariants({ variant, color, align, weight, size }),
          className,
        )}
        {...props}
      >
        {children}
      </Component>
    );
  },
);

Typography.displayName = 'Typography';

export { Typography };
